import type {PromptSuggestions, PromptSuggestionsValue} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import type {ReactElement} from 'react';
import React from 'react';
import styles from './ai-redesign-prompt-suggestions.module.scss';
import {PillButton} from '@Components/pill-button';
import {
  getPromptSuggestionsEnum,
  getPromptSuggestionsPillTitle,
  getPromptSuggestionsReplacementPrompt,
  RECENT_PROMPT_DISPLAY_LENGTH,
  RECENT_PROMPT_SUGGESTION_PILL_ICON,
  RECENT_PROMPT_SUGGESTION_PILL_KEY,
} from '@Libraries/ai-redesign-prompt-box-library';
import {PillButtonSize} from '@Components/pill-button/pill-button.types';
import {TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import type {IconProps} from '@Components/icon-v2';
import {motion} from 'framer-motion';

interface AiRedesignPromptSuggestionsProps {
  handlePromptChange: (prompt: string) => void;
  promptSuggestions?: PromptSuggestions;
  recentPrompt?: string;
}

export function AiRedesignPromptSuggestions({promptSuggestions, handlePromptChange, recentPrompt}: AiRedesignPromptSuggestionsProps): ReactElement {
  const getSuggestionPill = (key: string, displayText: string, replacementText: string, iconProps?: IconProps): ReactElement => {
    return (
      <div
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
      >
        <PillButton
          key={key}
          text={displayText}
          size={PillButtonSize.SMALL}
          textSize={TextSize.XXSMALL}
          leftIconProps={iconProps}
          onClick={(_id, e) => {
            e?.stopPropagation();
            handlePromptChange(replacementText);
          }}
        />
      </div>
    );
  };

  const getPromptSuggestionPills = (): ReactElement | undefined => {
    if (!promptSuggestions) {
      return;
    }

    return (
      <>
        {Object.values(getPromptSuggestionsEnum(promptSuggestions)).map((suggestion: PromptSuggestionsValue) =>
          getSuggestionPill(suggestion, getPromptSuggestionsPillTitle(suggestion), getPromptSuggestionsReplacementPrompt(suggestion))
        )}
      </>
    );
  };

  const getRecentPromptPillDisplayText = (originalText: string): string => {
    if (originalText.length <= RECENT_PROMPT_DISPLAY_LENGTH) {
      return originalText;
    }

    return originalText.slice(0, RECENT_PROMPT_DISPLAY_LENGTH) + '...';
  };

  const getRecentPrompt = (): ReactElement | undefined => {
    if (!recentPrompt) {
      return;
    }

    return getSuggestionPill(RECENT_PROMPT_SUGGESTION_PILL_KEY, getRecentPromptPillDisplayText(recentPrompt), recentPrompt, {
      icon: RECENT_PROMPT_SUGGESTION_PILL_ICON,
      size: IconSize.SIZE_ICON_16,
    });
  };

  const getPromptSuggestions = (): ReactElement => {
    return (
      <motion.div
        style={{ overflow: "hidden" }}
        initial={{opacity: 0, height: 0, y: -8}}
        animate={{opacity: 1, height: 'auto', y: 0}}
        exit={{opacity: 0, height: 0, y: -8}}
        transition={{duration: 0.2, ease: 'easeInOut'}}
      >
        <div className={styles.promptSuggestions}>
          {getRecentPrompt()}
          {getPromptSuggestionPills()}
        </div>
      </motion.div>
    );
  };

  return getPromptSuggestions();
}
